import React from "react";
import { Link, useParams } from "react-router-dom";
import { StorefrontService } from "src/app/services/StorefrontService";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

const STATUS_LABEL: { [key: string]: string } = {
	closed: "Sales closed",
	off: "Not on sale",
	onsale: "On sale",
	scheduled: "On sale soon"
};

const monthOf = (iso: string) =>
	new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric" }).toUpperCase();

const dayLine = (iso: string) => {
	const d = new Date(iso);
	return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", weekday: "short" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * The full schedule.
 *
 * Grouped by month and split upcoming/past, because "when are we at home next"
 * and "what did we do in September" are both things a supporter opens this to
 * answer, and a flat list answers neither well.
 *
 * Fixtures and events are separate tabs rather than one merged list. A club
 * runs a handful of events against a season of fixtures, and merging them
 * buries the events entirely.
 */
export function ShopSchedule() {
	const { slug = "" } = useParams();
	const [data, setData] = React.useState<any>(null);
	const [past, setPast] = React.useState(false);
	const [tab, setTab] = React.useState<"fixtures" | "events">("fixtures");
	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		let live = true;
		setLoading(true);
		(async () => {
			try {
				const res = await StorefrontService.getSchedule(slug, past);
				if (!live) return;
				if (res.ok) setData(await res.json());
			} catch {
				// The empty state below covers it.
			} finally {
				if (live) setLoading(false);
			}
		})();
		return () => { live = false; };
	}, [slug, past]);

	const items: any[] = (tab === "fixtures" ? data?.fixtures : data?.events) || [];

	// Month headings, in whatever order the server sent — ascending for
	// upcoming, descending for past. Re-sorting here would fight it.
	const groups: { month: string; items: any[] }[] = [];
	for (const item of items) {
		const month = monthOf(tab === "fixtures" ? item.kickOff : item.start);
		const last = groups[groups.length - 1];
		if (last && last.month === month) last.items.push(item);
		else groups.push({ items: [item], month });
	}

	return (
		<div className="shop has-nav" style={clubTheme(data?.club)}>
			<ShopHeader club={data?.club} slug={slug} />

			<div className="shop-inner">
				<div className="shop-toggle">
					<button className={past ? "" : "on"} onClick={() => setPast(false)} type="button">Upcoming</button>
					<button className={past ? "on" : ""} onClick={() => setPast(true)} type="button">Past</button>
				</div>

				<div className="shop-tabs">
					<button className={tab === "fixtures" ? "on" : ""} onClick={() => setTab("fixtures")} type="button">Fixtures</button>
					<button className={tab === "events" ? "on" : ""} onClick={() => setTab("events")} type="button">Events</button>
				</div>

				{loading && <div className="shop-result"><span className="shop-spinner" /></div>}

				{!loading && groups.length === 0 &&
					<p className="shop-empty">
						{past ? `Nothing here yet.` : `No ${tab} announced yet.`}
					</p>}

				{!loading && groups.map((group) => (
					<div key={group.month}>
						<h2 className="shop-section-title">{group.month}</h2>
						{group.items.map((item: any) => {
							const when = tab === "fixtures" ? item.kickOff : item.start;
							const to = `/club/${slug}/${tab === "fixtures" ? "fixture" : "event"}/${item.id}`;
							const played = tab === "fixtures" && item.homeScore != null && item.awayScore != null;

							return (
								<Link className="shop-card" key={item.id} to={to}>
									{(item.opponentBadgeUrl || item.logoUrl) &&
										<img alt="" className="shop-card-badge" src={item.opponentBadgeUrl || item.logoUrl} />}
									<div className="shop-card-body">
										<p className="shop-card-title">
											{tab === "fixtures"
												? `${item.isHome ? "v" : "away to"} ${item.opponent || "TBC"}`
												: item.name}
										</p>
										<div className="shop-card-meta">
											{dayLine(when)}
											{item.competition ? ` · ${item.competition}` : ""}
											{item.venue ? ` · ${item.venue}` : ""}
										</div>
										{!past && item.saleStatus !== "off" &&
											<span className={`shop-status ${item.saleStatus}`}>
												{STATUS_LABEL[item.saleStatus] || item.saleStatus}
											</span>}
									</div>
									{played &&
										<div className="shop-card-right">
											<span className="shop-score">{item.homeScore}&ndash;{item.awayScore}</span>
										</div>}
								</Link>
							);
						})}
					</div>
				))}
			</div>

			<ShopNav slug={slug} />
		</div>
	);
}
